import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Person as PersonIcon } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { CartItem, CustomerDetails, Table } from "../types";
import { petpooja } from "../posPetpoojaTheme";
import customerAPI, { Customer } from "../../../services/customerAPI";

type CustomerMode = "walkin" | "b2b";

interface CustomerDialogProps {
  open: boolean;
  selectedTable: Table | null;
  cartItems: CartItem[];
  customerDetails: CustomerDetails;
  onClose: () => void;
  onCustomerDetailsChange: (details: CustomerDetails) => void;
  onConfirm: () => void;
}

const CustomerDialog: React.FC<CustomerDialogProps> = ({
  open,
  selectedTable,
  cartItems,
  customerDetails,
  onClose,
  onCustomerDetailsChange,
  onConfirm,
}) => {
  const [mode, setMode] = useState<CustomerMode>(
    customerDetails.customerId ? "b2b" : "walkin"
  );
  const [search, setSearch] = useState("");

  const { data: customers = [], isLoading } = useQuery({
    queryKey: ["customer-search", search],
    queryFn: () =>
      customerAPI.getCustomers({
        search,
        pageSize: 20,
        pageNo: 1,
      }),
    enabled: mode === "b2b" && search.trim().length >= 2,
    select: (res) => res.data,
  });

  const subtotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const applyCustomer = (c: Customer) => {
    onCustomerDetailsChange({
      name: c.name,
      phone: c.phone || "",
      email: c.email || "",
      customerId: c.id,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, overflow: "hidden" },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: petpooja.header,
          color: petpooja.headerText,
          display: "flex",
          alignItems: "center",
          gap: 1,
          py: 1.5,
        }}
      >
        <PersonIcon />
        Customer Details
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Customer type</InputLabel>
          <Select
            label="Customer type"
            value={mode}
            onChange={(e) => {
              const next = e.target.value as CustomerMode;
              setMode(next);
              if (next === "walkin") {
                onCustomerDetailsChange({
                  ...customerDetails,
                  customerId: null,
                });
              }
            }}
          >
            <MenuItem value="walkin">Walk-in</MenuItem>
            <MenuItem value="b2b">B2B customer</MenuItem>
          </Select>
        </FormControl>

        {mode === "b2b" && (
          <Autocomplete
            size="small"
            options={customers}
            loading={isLoading}
            getOptionLabel={(o) =>
              `${o.name}${o.gstin ? ` · ${o.gstin}` : ""}${o.phone ? ` · ${o.phone}` : ""}`
            }
            isOptionEqualToValue={(a, b) => a.id === b.id}
            inputValue={search}
            onInputChange={(_, v) => setSearch(v)}
            onChange={(_, c) => {
              if (c) applyCustomer(c);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search B2B customer (name/GSTIN)…"
                sx={{ mb: 2 }}
              />
            )}
            noOptionsText={
              search.trim().length < 2
                ? "Type 2+ characters"
                : "No customers found"
            }
          />
        )}

        <TextField
          fullWidth
          size="small"
          label="Customer Name"
          value={customerDetails.name}
          onChange={(e) =>
            onCustomerDetailsChange({
              ...customerDetails,
              name: e.target.value,
            })
          }
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          size="small"
          label="Phone"
          value={customerDetails.phone}
          onChange={(e) =>
            onCustomerDetailsChange({
              ...customerDetails,
              phone: e.target.value,
            })
          }
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          size="small"
          label="Email"
          value={customerDetails.email}
          onChange={(e) =>
            onCustomerDetailsChange({
              ...customerDetails,
              email: e.target.value,
            })
          }
        />

        {selectedTable && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Table: {selectedTable.name} · Items: {cartItems.length} · Subtotal ₹
              {subtotal.toFixed(2)}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={onConfirm}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerDialog;
